import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Search, CheckCircle2, XCircle, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  active: "bg-brand/15 text-brand",
  trial: "bg-brand-amber/15 text-brand-amber",
  expired: "bg-red-100 text-red-600",
  admin: "bg-slate-800 text-white",
};

export default function Admin() {
  const [email, setEmail] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError(""); setUser(null);
    try {
      const { data } = await api.get(`/admin/users/by-email/${encodeURIComponent(email.trim().toLowerCase())}`);
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Pengguna tidak ditemukan.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (access_status) => {
    setSaving(true); setError("");
    try {
      const { data } = await api.patch("/admin/users/access", { email: user.email, access_status });
      setUser(data);
      toast.success(access_status === "active" ? "Akses diaktifkan." : "Akses dinonaktifkan.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memperbarui status akses.");
      toast.error("Gagal memperbarui status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl animate-fade-up">
        <div className="flex items-center gap-2 text-brand">
          <ShieldCheck className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wide">Admin</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink" data-testid="admin-header">
          Aktivasi Akses Pengguna
        </h1>
        <p className="mt-2 text-base text-ink-soft leading-relaxed">
          Cari pengguna berdasarkan email, lalu ubah status akses menjadi aktif setelah pembayaran dikonfirmasi.
        </p>

        <form onSubmit={lookup} className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label htmlFor="admin-email">Email pengguna</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="pengguna@email.com" className="mt-1.5 h-11" />
          </div>
          <Button type="submit" data-testid="admin-lookup-btn" disabled={loading} className="h-11 gap-2 bg-brand hover:bg-brand-hover sm:mt-6">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Cari
          </Button>
        </form>

        {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-error">{error}</div>}

        {user && (
          <div className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="admin-user-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand"><UserCog className="h-5 w-5" /></span>
                <div>
                  <div className="font-heading text-base font-semibold text-ink" data-testid="admin-user-name">{user.name}</div>
                  <div className="text-sm text-ink-muted" data-testid="admin-user-email">{user.email}</div>
                </div>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLE[user.access_status] || "bg-slate-100 text-slate-600"}`} data-testid="admin-user-status">
                {user.access_status}
              </span>
            </div>

            {user.role === "admin" ? (
              <p className="mt-5 rounded-lg bg-slate-50 px-4 py-3 text-sm text-ink-muted">Akun ini adalah admin — status akses tidak dapat diubah.</p>
            ) : (
              <div className="mt-5 flex flex-wrap gap-3">
                <Button onClick={() => setStatus("active")} disabled={saving || user.access_status === "active"}
                  data-testid="admin-activate-btn" className="gap-2 bg-brand hover:bg-brand-hover">
                  <CheckCircle2 className="h-4 w-4" /> Aktifkan Akses
                </Button>
                <Button onClick={() => setStatus("trial")} disabled={saving || user.access_status === "trial"}
                  data-testid="admin-deactivate-btn" variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-600">
                  <XCircle className="h-4 w-4" /> Nonaktifkan
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
